import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import {
	PlacementBackend,
	PlacementFrontend,
	type PlacementFrontendProps,
} from '@post-office/placements--growth/confluence-side-nav';
import { type WaitForTriggerFunction } from '@post-office/placement-contexts/hooks/use-wait-for-trigger';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import {
	useFirePostOfficeTrigger,
	useTrigger,
} from '@atlassian/post-office-context/post-office-trigger';
import { useCallback, useEffect } from 'react';

import {
	LOOM_MEETING_NUDGE_SIDENAV_SHOWN,
	LOOM_MEETING_NUDGE_SIDENAV_TRIGGER_ID,
	LOOM_MEETING_NUDGE_TITLE_SHOWN,
} from '../../../reminder-event';
import { type MessageTemplateContext } from '../../..';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { useLoomMeetingNudgeAnalytics } from '../../../analytics';
import { MeetingNudgeBanner } from './MeetingNudgeBanner';

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	flags: NonNullable<PlacementFrontendProps['flags']>;
	// Injected by the placement framework for client-triggered (poTrigger) messages.
	waitForTrigger?: WaitForTriggerFunction;
};

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: noValidation(),
		stage: () => ({
			goalMetadata: {
				primaryGoal: {
					goalId: 'clickthrough',
				},
			},
		}),
	},
}));

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({ cloudId, handleOnDismissed, flags, waitForTrigger }: MessageFrontendProps) => {
			const { fireNudgeSeen } = useLoomMeetingNudgeAnalytics('sidenav');
			// Confluence flips this active via useFirePostOfficeTrigger; a React state
			// update on the shared TriggerContext, so the banner shows without a reload.
			const { isActive } = useTrigger(LOOM_MEETING_NUDGE_SIDENAV_TRIGGER_ID);
			const { isActive: hasTitleNudgeShown } = useTrigger(LOOM_MEETING_NUDGE_TITLE_SHOWN);

			// Handle to the same trigger so we can reset it on teardown.
			const { fireTrigger } = useFirePostOfficeTrigger({
				triggerId: LOOM_MEETING_NUDGE_SIDENAV_TRIGGER_ID,
			});
			const resetTrigger = useCallback(() => {
				fireTrigger({ isActive: false });
			}, [fireTrigger]);
			const { fireTrigger: fireSidenavNudgeShown } = useFirePostOfficeTrigger({
				triggerId: LOOM_MEETING_NUDGE_SIDENAV_SHOWN,
			});
			const handleMessageShown = useCallback(() => {
				fireSidenavNudgeShown({ isActive: true });
				fireNudgeSeen();
			}, [fireNudgeSeen, fireSidenavNudgeShown]);

			useEffect(() => {
				if (hasTitleNudgeShown) {
					resetTrigger();
				}
			}, [hasTitleNudgeShown, resetTrigger]);

			if (!waitForTrigger) {
				return null;
			}

			const messageContent = (
				<MeetingNudgeBanner cloudId={cloudId} handleOnDismissed={handleOnDismissed} flags={flags} />
			);

			// waitForTrigger shows the message once isActive is true; on stop/dismiss its
			// onTeardown resets the trigger to false so product code can re-fire it.
			return waitForTrigger({
				isTriggerActive: !!isActive && !hasTitleNudgeShown,
				messageContent,
				onMessageShown: handleMessageShown,
				onTeardown: resetTrigger,
			});
		},
	},
});
